// @/components/providers/LanguageSelector.jsx

/**
 * LanguageSelector Module
 * 
 * Utility functions for managing language/locale selection and preferences.
 * Provides core functionality for the language system throughout the application.
 * 
 * Features:
 * - Comprehensive language metadata with native and English names
 * - Persistent storage of language preferences in localStorage
 * - Browser language detection as fallback
 * - Event dispatching for application-wide language changes
 * - Utility functions for language validation and metadata retrieval
 * - Multiple language support with ISO codes and flag mappings
 * - Fallback to English when preferred language is unavailable
 * - Graceful error handling for storage access issues
 * - Support for client and server-side rendering (SSR safety)
 * - Robust locale validation against supported languages
 * 
 * @module components/providers/LanguageSelector
 */

/**
 * Default supported languages with their metadata
 * @type {Array<Object>}
 */
const SUPPORTED_LOCALES = [
  { name: 'English', englishName: null, code: 'en', flag: 'gb' },
  { name: 'Deutsch', englishName: "German", code: 'de', flag: 'de' },
  { name: 'Español', englishName: "Spanish", code: 'es', flag: 'es' },
  { name: 'Français', englishName: "French", code: 'fr', flag: 'fr' },
  { name: 'Português', englishName: "Portuguese", code: 'pt', flag: 'pt' },
  { name: '中文', englishName: "Chinese (Simplified)", code: 'zh-CN', flag: 'cn' },
];

/**
 * Storage key used for persisting language preference
 * @type {string}
 * @private
 */
const STORAGE_KEY = 'userLocale';

/**
 * Get the current user's preferred locale
 * 
 * @returns {string} Locale code (e.g., 'en', 'de')
 */
export const getUserLocale = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  try {
    const storedLocale = localStorage.getItem(STORAGE_KEY);

    // Migration: Update old 'cn' to 'zh-CN'
    if (storedLocale === 'cn') {
      localStorage.setItem(STORAGE_KEY, 'zh-CN');
      return 'zh-CN';
    }

    if (storedLocale) {
      return storedLocale;
    }

    // Fall back to browser's language if available
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && SUPPORTED_LOCALES.some(locale => locale.code === browserLang)) {
      return browserLang;
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error accessing localStorage:', error);
    }
  }

  // Default to English
  return 'en';
};

/**
 * Set the user's preferred locale
 * 
 * @param {string} locale - Locale code to set (e.g., 'en', 'de')
 * @returns {boolean} Success status
 */
export const setUserLocale = (locale) => {
  if (typeof window === 'undefined') {
    return false; // Can't set in SSR
  }

  try {
    // Make sure it's a supported locale
    if (!SUPPORTED_LOCALES.some(l => l.code === locale)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Locale ${locale} is not supported. Falling back to 'en'.`);
      }
      locale = 'en';
    }

    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, locale);

    // Dispatch an event for components to listen to
    window.dispatchEvent(
      new CustomEvent('localeChange', {
        detail: {
          locale,
          previousLocale: getUserLocale()
        }
      })
    );

    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error setting locale:', error);
    }
    return false;
  }
};

/**
 * Get all supported locales
 * 
 * @returns {Array<Object>} Array of locale objects with name, code, and flag properties
 */
export const getSupportedLocales = () => {
  return SUPPORTED_LOCALES;
};

/**
 * Check if a locale is supported
 * 
 * @param {string} locale - Locale code to check
 * @returns {boolean} True if the locale is supported
 */
export const isLocaleSupported = (locale) => {
  return SUPPORTED_LOCALES.some(l => l.code === locale);
};

/**
 * Get locale details by code
 * 
 * @param {string} localeCode - Locale code to look up
 * @returns {Object|null} Locale object or null if not found
 */
export const getLocaleDetails = (localeCode) => {
  return SUPPORTED_LOCALES.find(l => l.code === localeCode) || null;
};